import { useState, useEffect } from "react";
import axios from "axios";
import { API, USER_ID } from "../App";
import { 
  MagnifyingGlass, 
  Plus, 
  Trash,
  Sun,
  SunHorizon,
  Moon,
  Cookie,
  Fire,
  Lightning,
  Bread,
  Drop,
  X,
  CaretDown,
  FunnelSimple,
  Barcode
} from "@phosphor-icons/react";
import { toast } from "sonner";
import BarcodeScanner from "../components/BarcodeScanner";

const MEAL_TYPES = [
  { id: "breakfast", label: "Petit-déjeuner", icon: Sun, color: "meal-breakfast" },
  { id: "lunch", label: "Déjeuner", icon: SunHorizon, color: "meal-lunch" },
  { id: "dinner", label: "Dîner", icon: Moon, color: "meal-dinner" },
  { id: "snack", label: "Collation", icon: Cookie, color: "meal-snack" },
];

const Nutrition = ({ profile }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [meals, setMeals] = useState([]);
  const [foods, setFoods] = useState([]);
  const [categories, setCategories] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedMealType, setSelectedMealType] = useState("breakfast");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [dailySummary, setDailySummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMeals();
    fetchFoods();
    fetchSummary();
    fetchCategories();
  }, [selectedDate]);

  const fetchMeals = async () => {
    try {
      const response = await axios.get(`${API}/meals/${USER_ID}?date=${selectedDate}`);
      setMeals(response.data);
    } catch (error) {
      console.error("Error fetching meals:", error);
    }
  };

  const fetchFoods = async () => {
    try {
      let url = `${API}/foods?limit=100`;
      if (searchQuery) url += `&search=${searchQuery}`;
      if (selectedCategory) url += `&category=${selectedCategory}`;
      const response = await axios.get(url);
      setFoods(response.data);
    } catch (error) {
      console.error("Error fetching foods:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/foods/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/meals/${USER_ID}/summary?date=${selectedDate}`);
      setDailySummary(response.data);
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchFoods();
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, selectedCategory]);

  const handleAddMeal = async () => {
    if (!selectedFood) return;

    try {
      await axios.post(`${API}/meals`, {
        user_id: USER_ID,
        date: selectedDate,
        meal_type: selectedMealType,
        food_name: selectedFood.name,
        food_id: selectedFood.id,
        calories: selectedFood.calories,
        protein: selectedFood.protein,
        carbs: selectedFood.carbs,
        fat: selectedFood.fat,
        fiber: selectedFood.fiber || 0,
        quantity: quantity,
        serving_size: selectedFood.serving_size,
      });
      
      toast.success("Aliment ajouté !");
      setShowAddModal(false);
      setSelectedFood(null);
      setQuantity(1);
      setSearchQuery("");
      fetchMeals();
      fetchSummary();
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
      console.error("Error adding meal:", error);
    }
  };

  const handleScannedProduct = async (product) => {
    try {
      await axios.post(`${API}/meals`, {
        user_id: USER_ID,
        date: selectedDate,
        meal_type: selectedMealType,
        food_name: product.name,
        food_id: product.barcode,
        calories: product.calories,
        protein: product.protein,
        carbs: product.carbs,
        fat: product.fat,
        fiber: product.fiber || 0,
        quantity: product.quantity,
        serving_size: product.serving_size,
      });
      
      toast.success(`${product.name} ajouté !`);
      setShowScanner(false);
      fetchMeals();
      fetchSummary();
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
      console.error("Error adding scanned product:", error);
    }
  };

  const handleDeleteMeal = async (mealId) => {
    try {
      await axios.delete(`${API}/meals/${mealId}`);
      toast.success("Aliment supprimé");
      fetchMeals();
      fetchSummary();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
      console.error("Error deleting meal:", error);
    }
  };

  const getMealsByType = (type) => meals.filter(m => m.meal_type === type);

  const goals = dailySummary?.goals || { calories: 2000, protein: 150, carbs: 250, fat: 65 };
  const totals = dailySummary?.totals || { calories: 0, protein: 0, carbs: 0, fat: 0 };

  return (
    <div className="p-4 md:p-8 animate-fadeIn max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-3xl uppercase tracking-tight text-charcoal">Nutrition</h1>
        <p className="text-gray-500">Garde le contrôle sur tes repas, tes macros et ton rythme alimentaire.</p>
      </div>

      {/* Date & Add Button */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="px-4 py-3 rounded-xl border border-gray-200 bg-white text-charcoal"
          data-testid="date-selector"
        />
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center justify-center gap-2"
          data-testid="add-meal-button"
        >
          <Plus size={20} weight="bold" />
          Ajouter un aliment
        </button>
        <button
          onClick={() => setShowScanner(true)}
          className="btn-secondary flex items-center justify-center gap-2"
          data-testid="scan-barcode-button"
        >
          <Barcode size={20} weight="bold" />
          Scanner un produit
        </button>
      </div>

      {/* Daily Summary */}
      <div className="card p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-charcoal">Résumé du jour</h2>
          <span className="text-sm text-gray-500">{Math.round(totals.calories)} / {goals.calories} kcal</span>
        </div>
        
        <div className="progress-bar mb-6">
          <div 
            className="progress-bar-fill" 
            style={{ width: `${Math.min((totals.calories / goals.calories) * 100, 100)}%` }} 
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="text-center" data-testid="summary-protein">
            <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-100 text-red-600 text-sm font-medium mb-2">
              <Lightning size={14} weight="fill" />
              Protéines
            </div>
            <p className="text-xl font-bold text-charcoal">{Math.round(totals.protein)}g</p>
            <p className="text-xs text-gray-500">/ {goals.protein}g</p>
          </div>

          <div className="text-center" data-testid="summary-carbs">
            <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-teal-100 text-teal-600 text-sm font-medium mb-2">
              <Bread size={14} weight="fill" />
              Glucides
            </div>
            <p className="text-xl font-bold text-charcoal">{Math.round(totals.carbs)}g</p>
            <p className="text-xs text-gray-500">/ {goals.carbs}g</p>
          </div>

          <div className="text-center" data-testid="summary-fat">
            <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-100 text-amber-600 text-sm font-medium mb-2">
              <Drop size={14} weight="fill" />
              Lipides
            </div>
            <p className="text-xl font-bold text-charcoal">{Math.round(totals.fat)}g</p>
            <p className="text-xs text-gray-500">/ {goals.fat}g</p>
          </div>
        </div>
      </div>

      {/* Meals by Type */}
      <div className="space-y-4">
        {MEAL_TYPES.map((mealType) => {
          const Icon = mealType.icon;
          const typeMeals = getMealsByType(mealType.id);
          const typeCalories = typeMeals.reduce((sum, m) => sum + m.calories * m.quantity, 0);

          return (
            <div 
              key={mealType.id} 
              className="card overflow-hidden"
              data-testid={`meal-section-${mealType.id}`}
            >
              <div className={`${mealType.color} px-5 py-4 flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <Icon size={22} weight="fill" className="text-white" />
                  <span className="font-semibold text-white">{mealType.label}</span>
                </div>
                <span className="text-sm text-white/90 font-medium">{Math.round(typeCalories)} kcal</span>
              </div>
              
              <div className="p-4">
                {typeMeals.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-4">
                    Aucun aliment ajouté
                  </p>
                ) : (
                  <div className="space-y-2">
                    {typeMeals.map((meal) => (
                      <div 
                        key={meal.id} 
                        className="flex items-center justify-between p-3 rounded-xl bg-cream"
                        data-testid={`meal-item-${meal.id}`}
                      >
                        <div className="flex-1">
                          <p className="font-medium text-charcoal">{meal.food_name}</p>
                          <p className="text-xs text-gray-500">
                            {meal.quantity > 1 && `${meal.quantity}x `}{meal.serving_size}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold text-charcoal">{Math.round(meal.calories * meal.quantity)} kcal</p>
                            <div className="flex gap-2 text-xs">
                              <span className="text-red-500">P:{Math.round(meal.protein * meal.quantity)}g</span>
                              <span className="text-teal-500">G:{Math.round(meal.carbs * meal.quantity)}g</span>
                              <span className="text-amber-500">L:{Math.round(meal.fat * meal.quantity)}g</span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteMeal(meal.id)}
                            className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                            data-testid={`delete-meal-${meal.id}`}
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <button
                  onClick={() => {
                    setSelectedMealType(mealType.id);
                    setShowAddModal(true);
                  }}
                  className="mt-3 w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-gray-400 hover:text-gold hover:border-gold transition-colors flex items-center justify-center gap-2"
                  data-testid={`add-to-${mealType.id}`}
                >
                  <Plus size={18} />
                  Ajouter
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Food Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="add-food-modal">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-display text-xl text-charcoal">Ajouter un aliment</h2>
              <button 
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedFood(null);
                  setQuantity(1);
                  setSearchQuery("");
                }}
                className="p-2 text-gray-400 hover:text-charcoal rounded-lg hover:bg-gray-100"
                data-testid="close-modal"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-5 max-h-[70vh] overflow-y-auto">
              {/* Meal Type Selector */}
              <div className="mb-4">
                <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block font-medium">Type de repas</label>
                <div className="grid grid-cols-4 gap-2">
                  {MEAL_TYPES.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => setSelectedMealType(type.id)}
                        className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                          selectedMealType === type.id 
                            ? 'border-gold bg-gold-light' 
                            : 'border-gray-200 hover:border-gold'
                        }`}
                        data-testid={`select-meal-type-${type.id}`}
                      >
                        <Icon size={20} className={selectedMealType === type.id ? 'text-gold' : 'text-gray-400'} />
                        <span className="text-xs text-charcoal">{type.label.split('-')[0]}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Search */}
              <div className="relative mb-4">
                <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un aliment..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 text-charcoal placeholder:text-gray-400"
                  data-testid="food-search-input"
                />
              </div>

              {/* Category Filter */}
              <div className="mb-4">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-charcoal"
                  data-testid="category-filter"
                >
                  <option value="">Toutes les catégories</option>
                  {Object.keys(categories).map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Food List */}
              <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                {foods.map((food) => (
                  <button
                    key={food.id}
                    onClick={() => setSelectedFood(food)}
                    className={`food-item w-full text-left ${selectedFood?.id === food.id ? 'selected' : ''}`}
                    data-testid={`food-item-${food.id}`}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-charcoal">{food.name}</p>
                      <p className="text-xs text-gray-500">{food.serving_size} • {food.category}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gold">{food.calories} kcal</p>
                      <p className="text-xs text-gray-500">
                        P:{food.protein}g | G:{food.carbs}g | L:{food.fat}g
                      </p>
                    </div>
                  </button>
                ))}
                {foods.length === 0 && (
                  <p className="text-center text-gray-400 py-8">Aucun aliment trouvé</p>
                )}
              </div>

              {/* Selected Food & Quantity */}
              {selectedFood && (
                <div className="bg-cream rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-medium text-charcoal">{selectedFood.name}</span>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setQuantity(Math.max(0.5, quantity - 0.5))}
                        className="w-10 h-10 rounded-xl border border-gray-200 bg-white text-charcoal hover:border-gold flex items-center justify-center"
                        data-testid="decrease-quantity"
                      >
                        -
                      </button>
                      <span className="font-bold text-charcoal w-12 text-center">{quantity}</span>
                      <button
                        onClick={() => setQuantity(quantity + 0.5)}
                        className="w-10 h-10 rounded-xl border border-gray-200 bg-white text-charcoal hover:border-gold flex items-center justify-center"
                        data-testid="increase-quantity"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-2 text-center mb-4">
                    <div className="bg-white rounded-lg p-2">
                      <p className="text-lg font-bold text-gold">{Math.round(selectedFood.calories * quantity)}</p>
                      <p className="text-xs text-gray-500">kcal</p>
                    </div>
                    <div className="bg-white rounded-lg p-2">
                      <p className="text-lg font-bold text-red-500">{Math.round(selectedFood.protein * quantity)}g</p>
                      <p className="text-xs text-gray-500">Prot.</p>
                    </div>
                    <div className="bg-white rounded-lg p-2">
                      <p className="text-lg font-bold text-teal-500">{Math.round(selectedFood.carbs * quantity)}g</p>
                      <p className="text-xs text-gray-500">Gluc.</p>
                    </div>
                    <div className="bg-white rounded-lg p-2">
                      <p className="text-lg font-bold text-amber-500">{Math.round(selectedFood.fat * quantity)}g</p>
                      <p className="text-xs text-gray-500">Lip.</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleAddMeal}
                    className="w-full btn-primary"
                    data-testid="confirm-add-meal"
                  >
                    Ajouter au {MEAL_TYPES.find(t => t.id === selectedMealType)?.label}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Barcode Scanner */}
      {showScanner && (
        <BarcodeScanner 
          onProductFound={handleScannedProduct}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
};

export default Nutrition;
